import React, { createContext, useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import apiClient from '../services/apiClient';
import { getStoredReferral } from '../utils/couponReferral';

const AuthContext = createContext();

let API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
if (API_BASE_URL.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
  API_BASE_URL = API_BASE_URL.replace('http://', 'https://');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = sessionStorage.getItem('meli_user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    try {
      const cached = sessionStorage.getItem('meli_user_cache');
      return !cached;
    } catch {
      return true;
    }
  });
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken'));
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // null = unknown/loading, else the /me status string
  const [subscriptionIsActive, setSubscriptionIsActive] = useState(false); // backend-computed: status AND current_period_end both check out
  const [tierUpgrade, setTierUpgrade] = useState(null); // non-null when last month's order volume outgrew the current tier
  const [affiliateDiscount, setAffiliateDiscount] = useState(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(() => !!localStorage.getItem('adminToken'));
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    try {
      const cached = localStorage.getItem('impersonatedUser');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) {
      sessionStorage.setItem('meli_user_cache', JSON.stringify(user));
    }
  }, [user]);

  const fetchSubscriptionStatus = async (token) => {
    if (!token) return;
    try {
      const response = await apiClient.get('/api/subscriptions/me');
      setSubscriptionStatus(response.data.status || 'none');
      setSubscriptionIsActive(!!response.data.is_active);
      setAffiliateDiscount(response.data.affiliate_discount || null);
      setTierUpgrade(response.data.upgrade_required ? {
        currentTier: response.data.tier || 'entrepreneur',
        requiredTier: response.data.required_tier,
        ordersLastMonth: response.data.orders_last_month,
        discountPercentOff: response.data.discount_percent_off || null,
        discountEndsAt: response.data.discount_ends_at || null,
      } : null);
    } catch (error) {
      setSubscriptionStatus('none');
      setSubscriptionIsActive(false);
      setTierUpgrade(null);
      setAffiliateDiscount(null);
    }
  };

  const authInitialized = useRef(false);
  useEffect(() => {
    if (authInitialized.current) return;
    authInitialized.current = true;

    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');

      if (token) {
        setAuthToken(token);
        fetchSubscriptionStatus(token);

        const cachedUser = sessionStorage.getItem('meli_user_cache');
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
          setLoading(false);
          verifyTokenInBackground(token);
          return;
        }

        try {
          const response = await apiClient.get('/api/auth/me-session');
          setUser(response.data);
        } catch (error) {
          try {
            const response = await apiClient.get('/api/auth/me');
            setUser(response.data);
          } catch (err) {
            console.error('Failed to fetch user:', err);
            logout();
          }
        }
      }

      setLoading(false);
    };

    const verifyTokenInBackground = async (token) => {
      try {
        const response = await apiClient.get('/api/auth/me-session');
        setUser(response.data);
      } catch (error) {
        try {
          const response = await apiClient.get('/api/auth/me');
          setUser(response.data);
        } catch (err) {
          sessionStorage.removeItem('meli_user_cache');
          logout();
        }
      }
    };

    initializeAuth();
  }, []);

  // Email/Password Login
  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData, subscription_status, restricted } = response.data;

      localStorage.setItem('authToken', access_token);
      setAuthToken(access_token);
      setUser(userData);

      await fetchSubscriptionStatus(access_token);

      return { success: true, user: userData, subscriptionStatus: subscription_status, restricted: !!restricted };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      return { success: false, error: message };
    }
  };

  // Register
  const register = async (email, password, name) => {
    try {
      const referralCode = getStoredReferral();
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, {
        email,
        password,
        name,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });

      const { access_token, user: userData } = response.data;

      localStorage.setItem('authToken', access_token);
      setAuthToken(access_token);
      setUser(userData);
      fetchSubscriptionStatus(access_token);

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrarse';
      return { success: false, error: message };
    }
  };

  // Forgot Password
  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error al enviar correo' };
    }
  };

  // Reset Password
  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al restablecer contraseña';
      return { success: false, error: message };
    }
  };

  // MeLi OAuth Login (redirect)
  const loginWithMeli = (site) => {
    let url = `${API_BASE_URL}/api/auth/start?site=${site}`;
    if (user?.id) {
      url += `&pending_user=${user.id}`;
    }
    window.location.href = url;
  };

  const login = loginWithMeli;

  const logout = () => {
    // If impersonating, also clear impersonation state
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonatedUser');
    setIsImpersonating(false);
    setImpersonatedUser(null);

    localStorage.removeItem('authToken');
    sessionStorage.removeItem('meli_user_cache');
    sessionStorage.removeItem('meli_data_cache');
    // Next login shows every live notification again (LoginNotificationOverlay).
    sessionStorage.removeItem('shown_login_notifications');
    sessionStorage.removeItem('viewed_bell_notifications');
    setUser(null);
    setAuthToken(null);
    setSubscriptionStatus(null);
    setSubscriptionIsActive(false);
    setTierUpgrade(null);
    setAffiliateDiscount(null);

    if (authToken) {
      axios.post(`${API_BASE_URL}/api/logout`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      }).catch(console.error);
    }
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await apiClient.get('/api/auth/me-session');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  // ============== IMPERSONATION ==============
  const startImpersonation = async (email) => {
    const currentToken = localStorage.getItem('authToken');
    try {
      const response = await apiClient.post(
        `/api/superadmin/impersonate/${encodeURIComponent(email)}`,
        {}
      );

      const { token: newToken, user: targetUser } = response.data;

      // Save admin's original token
      localStorage.setItem('adminToken', currentToken);
      localStorage.setItem('impersonatedUser', JSON.stringify(targetUser));

      // Switch to impersonated user
      localStorage.setItem('authToken', newToken);
      sessionStorage.removeItem('meli_user_cache');
      sessionStorage.removeItem('meli_data_cache');
      setAuthToken(newToken);
      setImpersonatedUser(targetUser);
      setIsImpersonating(true);
      fetchSubscriptionStatus(newToken);

      // Refresh user data with new token
      try {
        const userResp = await apiClient.get('/api/auth/me-session');
        setUser(userResp.data);
      } catch {
        setUser({ ...targetUser, role: targetUser.role });
      }

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al impersonar usuario';
      return { success: false, error: message };
    }
  };

  const stopImpersonation = () => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) return;

    // Restore admin token
    localStorage.setItem('authToken', adminToken);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonatedUser');
    sessionStorage.removeItem('meli_user_cache');
    sessionStorage.removeItem('meli_data_cache');

    setAuthToken(adminToken);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    fetchSubscriptionStatus(adminToken);

    // Re-fetch admin user data
    apiClient.get('/api/auth/me-session')
      .then(res => setUser(res.data)).catch(console.error);
  };

  const isAdmin = user?.role === 'super_admin';
  const isConsultant = user?.role === 'consultant';
  // Per-user feature flags (users.features in Mongo); super_admin has all.
  const hasFeature = (feature) => isAdmin || (user?.features || []).includes(feature);

  const value = {
    user,
    authToken,
    loading,
    login,
    loginWithEmail,
    loginWithMeli,
    register,
    forgotPassword,
    resetPassword,
    logout,
    refreshUser,
    isAdmin,
    isConsultant,
    hasFeature,
    isVip: user?.role === 'vip_user' || user?.role === 'super_admin',
    // Subscription (Stripe)
    subscriptionStatus,
    hasActiveSubscription: isAdmin || (subscriptionIsActive && !tierUpgrade),
    tierUpgrade: isAdmin ? null : tierUpgrade,
    // Referral discount ({ program: 'consultant' | 'friend', percent_off, months_remaining }) or null.
    affiliateDiscount,
    refreshSubscriptionStatus: () => fetchSubscriptionStatus(authToken || localStorage.getItem('authToken')),
    // Impersonation
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
